import React, { useState } from "react";
import { supabase } from "../supabaseClient";
import { FiMail, FiLock, FiEye, FiEyeOff, FiShield, FiCheckCircle } from "react-icons/fi";
import Button from "./ui/Button";
import Input from "./ui/Input";

export default function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      setError(error.message);
      setLoading(false);
    } else {
      onLogin(data.user);
    }
  };

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-950">
      {/* LEFT: branding */}
      <div className="relative hidden w-1/2 flex-col justify-between overflow-hidden bg-gradient-to-br from-primary-600 via-primary-700 to-secondary-700 p-12 lg:flex">
        {/* abstract background */}
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -right-24 -top-24 h-96 w-96 rounded-full bg-white/10 blur-3xl" />
          <div className="absolute -bottom-32 -left-24 h-96 w-96 rounded-full bg-white/10 blur-3xl" />
          <div className="absolute right-1/3 top-1/2 h-64 w-64 rounded-full bg-secondary-400/20 blur-3xl" />
        </div>

        <div className="relative z-10 flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/15 text-lg font-bold text-white backdrop-blur">
            XF
          </div>
          <div>
            <p className="text-sm font-bold text-white">XF Admin</p>
            <p className="text-xs text-white/70">Administration Panel</p>
          </div>
        </div>

        <div className="relative z-10 max-w-md">
          <h1 className="text-4xl font-bold leading-tight text-white">
            Kelola bisnis Anda dari satu dashboard premium.
          </h1>
          <p className="mt-4 text-base leading-relaxed text-white/80">
            Monitor toko, pembayaran, referal, penarikan, dan komunikasi pelanggan secara real-time dengan antarmuka yang bersih dan cepat.
          </p>

          <ul className="mt-8 space-y-3">
            {["Monitoring real-time", "Manajemen lisensi & toko", "Keamanan terintegrasi"].map((item) => (
              <li key={item} className="flex items-center gap-3 text-sm text-white/90">
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-white/15">
                  <FiCheckCircle className="h-3.5 w-3.5" />
                </span>
                {item}
              </li>
            ))}
          </ul>
        </div>

        <div className="relative z-10 flex items-center gap-2 text-xs text-white/60">
          <FiShield className="h-3.5 w-3.5" />
          Secure Admin Access
        </div>
      </div>

      {/* RIGHT: form */}
      <div className="flex w-full flex-col items-center justify-center p-6 lg:w-1/2 lg:p-12">
        <div className="w-full max-w-md">
          {/* Mobile brand */}
          <div className="mb-8 flex items-center gap-3 lg:hidden">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-primary-500 to-secondary-500 text-lg font-bold text-white shadow-soft">
              XF
            </div>
            <div>
              <p className="text-sm font-bold text-slate-900 dark:text-white">XF Admin</p>
              <p className="text-xs text-slate-500 dark:text-slate-400">Administration Panel</p>
            </div>
          </div>

          <div className="animate-fade-in-up rounded-2xl border border-slate-200/80 bg-white p-8 shadow-card dark:border-slate-800 dark:bg-slate-900">
            <div className="mb-6">
              <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Welcome back</h1>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Sign in to your admin account</p>
            </div>

            {error && (
              <div className="mb-5 rounded-xl border border-danger-200 bg-danger-50 px-4 py-3 text-sm text-danger-700 dark:border-danger-500/20 dark:bg-danger-500/10 dark:text-danger-300">
                {error}
              </div>
            )}

            <form onSubmit={handleLogin} className="space-y-4">
              <Input
                label="Email"
                type="email"
                required
                icon={FiMail}
                placeholder="admin@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
              />

              <div className="relative">
                <Input
                  label="Password"
                  type={showPassword ? "text" : "password"}
                  required
                  icon={FiLock}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-[34px] rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800"
                  aria-label={showPassword ? "Sembunyikan password" : "Tampilkan password"}
                >
                  {showPassword ? <FiEyeOff className="h-4 w-4" /> : <FiEye className="h-4 w-4" />}
                </button>
              </div>

              <Button type="submit" size="lg" loading={loading} className="w-full">
                {loading ? "Signing in..." : "Sign In"}
              </Button>
            </form>

            <div className="mt-6 flex items-center justify-center gap-2 text-xs text-slate-400 dark:text-slate-500">
              <FiShield className="h-3.5 w-3.5" />
              Secure Admin Access
            </div>
          </div>

          <p className="mt-6 text-center text-sm text-slate-400 dark:text-slate-500">XF Admin Panel v1.0</p>
        </div>
      </div>
    </div>
  );
}
